"use client";

import { useIsRestoring, useQuery } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/types";
import {
  DEFAULT_CONTENT_SETTINGS,
  type ContentSettings,
} from "@/lib/utils/content-settings/content-settings-defaults";
import {
  fetchTenantContentSettings,
  type TenantContentSettingsResult,
} from "@/lib/utils/content-settings/tenant-content-settings-api";

export type { ContentSettings };
export { DEFAULT_CONTENT_SETTINGS };

const EMPTY_RESULT: TenantContentSettingsResult = {
  settings: DEFAULT_CONTENT_SETTINGS,
  persistedKeys: new Set(),
};

function persistedKeysFromUnknown(
  value: unknown,
): ReadonlySet<keyof ContentSettings> {
  if (value instanceof Set) {
    return value as ReadonlySet<keyof ContentSettings>;
  }
  if (Array.isArray(value)) {
    return new Set(value as (keyof ContentSettings)[]);
  }
  return EMPTY_RESULT.persistedKeys;
}

interface IUseTenantContentSettingsGetProps {
  tenantId: number;
}

export const useTenantContentSettingsGet = (
  { tenantId }: IUseTenantContentSettingsGetProps,
  options?: { enabled?: boolean },
) => {
  const isRestoring = useIsRestoring();
  const {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [QueryKeys.TenantContentSettings, tenantId],
    queryFn: () => fetchTenantContentSettings(tenantId),
    enabled: options?.enabled !== false && !!tenantId && !isRestoring,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    placeholderData: EMPTY_RESULT,
  });

  return {
    data: data?.settings || DEFAULT_CONTENT_SETTINGS,
    persistedKeys: persistedKeysFromUnknown(data?.persistedKeys),
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
  };
};
